import { type FC, type ReactNode } from 'react';

import { Form, FormButtons, FormTextarea } from '@zen/Components/Form';
import FormScoreSelect from '@zen/Components/Form/FormScoreSelect';
import LucaAvatar from '@zen/Components/LucaAvatar';
import type { FormInstance } from '@zen/Components/types';
import { Button, Modal } from '@zen/DesignSystem';
import type { IOkOrErrorResult } from '@zen/utils/OkOrErrorResult';

import { type Question, questions } from './helpers';
import type { NetPromoterScoreSurveyValues } from './types';
import { validationSchema } from './validation';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: NetPromoterScoreSurveyValues) => Promise<IOkOrErrorResult>;
  onSuccess: () => void;
}

const NetPromoterScoreSurvey: FC<Props> = ({ isOpen, onClose, onSubmit, onSuccess }) => {
  const renderFields = ({ type, labels, name }: Question) => {
    if (type === 'score') {
      return <FormScoreSelect hintLabels={labels} name={name} />;
    }

    return <FormTextarea name={name} />;
  };

  const renderQuestions = () => {
    return questions.map((question) => (
      <div key={question.question}>
        <div className="font-bold mb-2">{question.question}</div>
        {renderFields(question)}
      </div>
    ));
  };

  const renderFormButtons = ({ isSubmitting }: FormInstance<NetPromoterScoreSurveyValues>): ReactNode => {
    return (
      <FormButtons isSubmitting={isSubmitting} layout="fixed" text="Submit feedback">
        <Button onClick={onClose} variant="secondary">
          Skip for now
        </Button>
      </FormButtons>
    );
  };

  const handleSubmit = (values: NetPromoterScoreSurveyValues): Promise<IOkOrErrorResult> => {
    return onSubmit(values);
  };

  return (
    <Modal
      closeOnClickAway={false}
      isOpen={isOpen}
      onClose={onClose}
      title="We'd love your feedback! Let us know how we're performing."
      width="medium"
    >
      <Form
        formButtons={renderFormButtons}
        formName="NetPromoterScoreSurvey"
        initialValues={{
          operationalServices: null,
          visibility: null,
          recommendation: null,
          marketConditions: null,
          additionalComments: ''
        }}
        onSubmit={handleSubmit}
        onSuccess={onSuccess}
        validationSchema={validationSchema}
      >
        {() => (
          <div className="flex flex-col space-y-4">
            {renderQuestions()}
            <div className="flex items-center space-x-2 justify-end mt-4">
              <LucaAvatar size="medium" />
              <div className="text-transparent bg-clip-text bg-sunset-base text-sm">Powered by Luca</div>
            </div>
          </div>
        )}
      </Form>
    </Modal>
  );
};

export default NetPromoterScoreSurvey;
